import type { PedaCategoryKey } from '../../types';
import { resolveElementMessages } from '../../systems/object-inspect-utils';

export type InspectObjectRole = 'anomaly' | 'decoy';

export interface InspectObjectConfig {
  id: string;
  image?: string;
  position: SceneElement['position'];
  inspectable: boolean;
  role: InspectObjectRole;
  reportMessage: string;
  dismissMessage: string;
  pedaShields: number;
  pedaCategory?: PedaCategoryKey;
}

export interface ObjectInspectSceneConfig {
  /** Texte affiché tant que la gate est fermée. */
  instruction: string;
  /** Texte affiché au-dessus de Continuer quand la gate est ouverte. */
  exitPrompt: string;
  /** Texte du bouton de sortie (exitChoice.text). */
  choicePrompt: string;
  continueGotoScene: string;
  completionDialogues: string[];
  objects: InspectObjectConfig[];
}

function extractCompletionDialogues(action: ClickAction | undefined): string[] {
  if (!action) return [];

  if (action.type === 'setFlag' && action.condition && action.nextAction) {
    const dialogues: string[] = [];
    let current: ClickAction | undefined = action.nextAction;
    while (current) {
      if (current.type === 'dialogue' && typeof current.payload === 'string') {
        dialogues.push(current.payload);
      }
      current = current.nextAction;
    }
    if (dialogues.length > 0) return dialogues;
  }

  return extractCompletionDialogues(action.nextAction);
}

function parseElement(element: SceneElement): InspectObjectConfig {
  const isAnomaly = (element.pedaShields ?? 0) > 0;
  const inspectable = Boolean(
    element.inspectable
    || element.reportMessage?.trim()
    || element.dismissMessage?.trim()
    || element.clickAction
    || element.dismissAction
  );

  const { reportMessage, dismissMessage } = resolveElementMessages(element);

  return {
    id: element.id,
    image: element.image,
    position: element.position,
    inspectable,
    role: isAnomaly ? 'anomaly' : 'decoy',
    reportMessage,
    dismissMessage,
    pedaShields: element.pedaShields ?? 0,
    pedaCategory: element.pedaCategory,
  };
}

export function defaultObjectInspectConfig(_sceneId: string): ObjectInspectSceneConfig {
  return {
    instruction: "Qu'est-ce qui cloche ? Cliquez sur les éléments suspects.",
    exitPrompt: '',
    choicePrompt: 'Continuer',
    continueGotoScene: '',
    completionDialogues: [],
    objects: [],
  };
}

export function parseObjectInspectScene(scene: Scene): ObjectInspectSceneConfig {
  const objects = (scene.elements ?? [])
    .filter((element) => element.type === 'object')
    .map((element) => parseElement(element));

  const observation = scene.type === 'observation' ? scene : null;

  let completionDialogues = observation?.completionDialogues ?? [];
  if (completionDialogues.length === 0) {
    const anomalyWithTail = (scene.elements ?? []).find(
      (element) => element.type === 'object' && (element.pedaShields ?? 0) > 0 && element.clickAction
    );
    if (anomalyWithTail?.clickAction) {
      completionDialogues = extractCompletionDialogues(anomalyWithTail.clickAction);
    }
  }

  const defaults = defaultObjectInspectConfig(scene.id);

  return {
    instruction: observation?.instruction ?? (scene.type === 'choice' ? (scene as { prompt?: string }).prompt : undefined) ?? defaults.instruction,
    exitPrompt: observation?.exitPrompt ?? defaults.exitPrompt,
    choicePrompt: observation?.exitChoice?.text ?? defaults.choicePrompt,
    continueGotoScene: scene.goto ?? '',
    completionDialogues,
    objects,
  };
}

export function applyObjectInspectScene(scene: Scene, config: ObjectInspectSceneConfig): Scene {
  const elements: SceneElement[] = config.objects.map((object) => {
    const element: SceneElement = {
      id: object.id,
      type: 'object',
      position: object.position,
      image: object.image,
    };

    if (object.role === 'anomaly' && object.pedaShields > 0) {
      element.pedaShields = object.pedaShields;
      if (object.pedaCategory) {
        element.pedaCategory = object.pedaCategory;
      }
    }

    if (object.inspectable) {
      const report = object.reportMessage.trim();
      const dismiss = object.dismissMessage.trim();
      if (report) {
        element.reportMessage = report;
      }
      if (dismiss) {
        element.dismissMessage = dismiss;
      }
      // Sans message, l'inspectabilité doit rester persistée explicitement.
      if (!report && !dismiss) {
        element.inspectable = true;
      }
    }

    return element;
  });

  const observation: ObservationScene = {
    id: scene.id,
    type: 'observation',
    background: scene.background,
    ...(scene.backgroundLoop !== undefined ? { backgroundLoop: scene.backgroundLoop } : {}),
    condition: scene.condition,
    instruction: config.instruction || undefined,
    exitPrompt: config.exitPrompt.trim() || undefined,
    completionDialogues: config.completionDialogues.length > 0 ? config.completionDialogues : undefined,
    elements,
    exitChoice: {
      text: config.choicePrompt || 'Continuer',
    },
    goto: config.continueGotoScene || undefined,
  };
  return observation;
}

export function ensureObjectInspectScene(scene: Scene): Scene {
  if (scene.type !== 'observation') return scene;
  if (!scene.exitChoice) {
    return applyObjectInspectScene(scene, parseObjectInspectScene(scene));
  }
  return scene;
}
